"use client";

import { useCallback, useEffect, useState } from "react";
import type { InventoryRow } from "@repo/utils/aiImagesInventory";

const ZOOM_STEPS: number[] = [1, 1.5, 2, 3, 4];
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

/**
 * Full-size preview for one asset, over the inventory table.
 *
 * The original url is used rather than a resized variant: the point of opening this is to
 * judge whether an image is AI-generated, and the image service's thumbnail is exactly the
 * detail-destroying version that makes that call impossible.
 */
/**
 * Storyblok's asset library, filtered to this asset's filename.
 *
 * The app has no documented deep link to a single asset, so this opens the library with
 * the filename as its search term — one click from the asset rather than a guessed url
 * that 404s. Falls back to nothing when the space id is not configured.
 */
function storyblokAssetUrl(
  spaceId: string | null,
  filename: string,
): string | null {
  if (!spaceId) return null;

  return `https://app.storyblok.com/#/me/spaces/${spaceId}/assets?search=${encodeURIComponent(
    filename,
  )}`;
}

const AiImagePreview = ({
  row,
  spaceId,
  onClose,
}: {
  row: InventoryRow;
  spaceId: string | null;
  onClose: () => void;
}) => {
  const [zoom, setZoom] = useState<number>(MIN_ZOOM);
  const sbUrl = storyblokAssetUrl(spaceId, row.filename);

  // Zoom resets per asset: carrying a 4x zoom into the next image would open it scrolled
  // into a corner with no indication why.
  useEffect(() => {
    setZoom(MIN_ZOOM);
  }, [row.id]);

  const step = useCallback((direction: 1 | -1) => {
    setZoom((current) => {
      if (direction === 1) {
        return ZOOM_STEPS.find((value) => value > current) ?? MAX_ZOOM;
      }

      return (
        [...ZOOM_STEPS].reverse().find((value) => value < current) ?? MIN_ZOOM
      );
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "+" || event.key === "=") step(1);
      if (event.key === "-") step(-1);
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose, step]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${row.filename || `asset ${row.id}`}`}
      // The backdrop itself dismisses, so it carries a cursor that says so.
      className="fixed inset-0 z-50 flex cursor-zoom-out flex-col bg-black/80"
      // Backdrop click closes; the click handler sits on the backdrop itself so a click on
      // the image or the toolbar (which stopPropagation is not needed for, being children
      // of a separate element) does not dismiss the dialog by accident.
      onClick={onClose}
    >
      <div
        className="flex cursor-default flex-wrap items-center gap-3 bg-black/60 p-3 text-sm text-white"
        onClick={(event) => event.stopPropagation()}
      >
        <span className="truncate font-mono text-xs">
          {row.filename || `asset ${row.id}`}
        </span>
        <button
          type="button"
          onClick={() => step(-1)}
          disabled={zoom <= MIN_ZOOM}
          className="cursor-pointer rounded border border-white/40 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Zoom out"
        >
          −
        </button>
        <span className="w-12 text-center tabular-nums">{zoom}×</span>
        <button
          type="button"
          onClick={() => step(1)}
          disabled={zoom >= MAX_ZOOM}
          className="cursor-pointer rounded border border-white/40 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          onClick={() => setZoom(MIN_ZOOM)}
          className="cursor-pointer rounded border border-white/40 px-2 py-1"
        >
          Reset
        </button>
        <a
          href={row.url}
          target="_blank"
          rel="noreferrer"
          className="cursor-pointer rounded border border-white/40 px-2 py-1"
        >
          Open original
        </a>
        {sbUrl && (
          <a
            href={sbUrl}
            target="_blank"
            rel="noreferrer"
            className="cursor-pointer rounded border border-white/40 px-2 py-1"
            title="Opens the asset library, searched for this filename"
          >
            View in Storyblok
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          className="ml-auto cursor-pointer rounded bg-white px-3 py-1 text-black"
        >
          Close
        </button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={row.url}
          alt={row.cleanAlt || row.filename}
          onClick={(event) => {
            event.stopPropagation();
            step(zoom >= MAX_ZOOM ? -1 : 1);
          }}
          // At 1x the image fits the viewport; past that it overflows and the container
          // scrolls, which is what makes inspecting a detail possible at all.
          style={zoom === 1 ? undefined : { width: `${zoom * 100}%` }}
          className={`mx-auto h-auto object-contain ${
            zoom === 1
              ? "max-h-full max-w-full cursor-zoom-in"
              : `max-w-none ${zoom >= MAX_ZOOM ? "cursor-zoom-out" : "cursor-zoom-in"}`
          }`}
        />
      </div>
    </div>
  );
};

export default AiImagePreview;
